import React, { useState, useEffect } from 'react';
import { useThemeTokens } from '../theme/ThemeProvider';
import { Button, Card, Tabs, Metric, Chart, DataTable, Progress } from './ui';

const Movimientos = () => {
  const { getComponent, getColor, getSpacing, getFontSize, getBorderRadius } = useThemeTokens();
  
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useSynthetic, setUseSynthetic] = useState(true);
  const [selectedAlmacenes, setSelectedAlmacenes] = useState(['A2', 'A3', 'A4', 'A5']);
  const [overviewData, setOverviewData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Datos de almacenes
  const almacenesDisponibles = ['A2', 'A3', 'A4', 'A5'];
  
  const styles = {
    container: {
      padding: getSpacing('xl'),
      fontFamily: getComponent('typography').fontFamilies.primary,
      minHeight: '100vh',
      backgroundColor: getColor('neutral.lightGray'),
    },
    header: {
      textAlign: 'center',
      marginBottom: getSpacing('xl'),
    },
    title: {
      fontSize: getFontSize('4xl'),
      fontWeight: '700',
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: getSpacing('md'),
    },
    subtitle: {
      fontSize: getFontSize('lg'),
      color: getColor('neutral.mediumGray'),
      maxWidth: '600px',
      margin: '0 auto',
    },
    sidebar: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('lg'),
      borderRadius: getBorderRadius('lg'),
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginBottom: getSpacing('lg'),
    },
    sidebarTitle: {
      fontSize: getFontSize('lg'),
      fontWeight: '600',
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md'),
      display: 'flex',
      alignItems: 'center',
      gap: getSpacing('sm'),
    },
    infoCard: {
      backgroundColor: getColor('primary.lightBlue'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      border: `1px solid ${getColor('primary.blue')}`,
    },
    successCard: {
      backgroundColor: getColor('accent.green'),
      color: getColor('neutral.white'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
    },
    controlSection: {
      marginBottom: getSpacing('lg'),
    },
    label: {
      display: 'block',
      fontSize: getFontSize('sm'),
      fontWeight: '500',
      color: getColor('neutral.darkGray'),
      marginBottom: getSpacing('xs'),
    },
    multiselect: {
      width: '100%',
      padding: getSpacing('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      fontSize: getFontSize('sm'),
      backgroundColor: getColor('neutral.white'),
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: getSpacing('sm'),
      marginBottom: getSpacing('sm'),
    },
    toggleSwitch: {
      position: 'relative',
      width: '50px',
      height: '24px',
      backgroundColor: useSynthetic ? getColor('primary.blue') : getColor('neutral.mediumGray'),
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
    },
    toggleButton: {
      position: 'absolute',
      top: '2px',
      left: useSynthetic ? '26px' : '2px',
      width: '20px',
      height: '20px',
      backgroundColor: getColor('neutral.white'),
      borderRadius: '50%',
      transition: 'left 0.3s',
    },
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: getSpacing('md'),
      marginBottom: getSpacing('lg'),
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: getSpacing('lg'),
      marginBottom: getSpacing('lg'),
    },
    placeholderContent: {
      textAlign: 'center',
      padding: getSpacing('xl'),
      color: getColor('neutral.mediumGray'),
      backgroundColor: getColor('neutral.white'),
      borderRadius: getBorderRadius('md'),
      border: `2px dashed ${getColor('neutral.lightGray')}`,
    },
    almacenExplanation: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('lg'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      border: `1px solid ${getColor('neutral.lightGray')}`,
    },
    explanationGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: getSpacing('md'),
      marginTop: getSpacing('md'),
    },
    explanationCard: {
      padding: getSpacing('md'),
      backgroundColor: getColor('neutral.lightGray'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
    },
    flowDiagram: {
      textAlign: 'center',
      padding: getSpacing('lg'),
      backgroundColor: getColor('neutral.lightGray'),
      borderRadius: getBorderRadius('md'),
      fontFamily: 'monospace',
      fontSize: getFontSize('sm'),
      marginTop: getSpacing('md'),
    },
  };

  // Simulación de datos de inventarios
  useEffect(() => {
    if (useSynthetic) {
      setOverviewData({
        total_stock: 125430,
        total_valor: 2847562.50,
        total_skus: 1247,
        productos_stock_critico: 23,
        resumen_almacenes: {
          A2: { Stock_Total: 45230, Valor_Total: 1023456.78 },
          A3: { Stock_Total: 32150, Valor_Total: 754321.45 },
          A4: { Stock_Total: 38970, Valor_Total: 892134.67 },
          A5: { Stock_Total: 9080, Valor_Total: 177649.60 },
        },
        last_connection: 'Conectado a datos sintéticos',
        total_records: 15432,
        tables: ['inventory', 'movements', 'programmatic_movements'],
      });
    }
  }, [useSynthetic]);

  const handleToggleSynthetic = () => {
    setUseSynthetic(!useSynthetic);
  };

  const handleAlmacenesChange = (e) => {
    const value = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedAlmacenes(value);
  };

  // Componente Dashboard General
  const DashboardTab = () => (
    <div>
      {/* Explicación del Sistema */}
      <div style={styles.almacenExplanation}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md'), color: getColor('neutral.black') }}>
          ℹ️ Sistema de Almacenes Gaero - Explicación Completa
        </h3>
        
        <p style={{ fontSize: getFontSize('sm'), color: getColor('neutral.darkGray'), marginBottom: getSpacing('md') }}>
          El sistema de Gaero maneja <strong>6 estados logísticos</strong> principales para cada pieza:
        </p>
        
        <div style={styles.explanationGrid}>
          <div style={styles.explanationCard}>
            <h4 style={{ fontSize: getFontSize('md'), color: getColor('neutral.black'), marginBottom: getSpacing('sm') }}>
              📦 Almacenes Principales
            </h4>
            <ul style={{ fontSize: getFontSize('sm'), color: getColor('neutral.darkGray'), lineHeight: '1.6' }}>
              <li><strong>A1</strong> - Plan de Envío: Tránsito proveedor → Gaero (5-30 días)</li>
              <li><strong>A2</strong> - Almacén Físico Gaero: Recepción y conteo físico</li>
              <li><strong>A3</strong> - Envío a Marketplace: Piezas enviadas, pendientes validación (1-7 días)</li>
            </ul>
          </div>
          
          <div style={styles.explanationCard}>
            <h4 style={{ fontSize: getFontSize('md'), color: getColor('neutral.black'), marginBottom: getSpacing('sm') }}>
              🏬 Almacenes Marketplace
            </h4>
            <ul style={{ fontSize: getFontSize('sm'), color: getColor('neutral.darkGray'), lineHeight: '1.6' }}>
              <li><strong>A4</strong> - Stock Publicado: Reconocido por marketplace, en venta</li>
              <li><strong>A5</strong> - Bodega Gaero: Devoluciones y retiros propios</li>
              <li><strong>A6</strong> - Retención por Incidencias: Producto retenido por problemas graves</li>
            </ul>
          </div>
        </div>
        
        <div style={styles.flowDiagram}>
          <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>🔄 Flujo Típico de Movimientos</h4>
          <pre style={{ margin: 0, fontSize: getFontSize('sm') }}>
{`A1 → A2 → A3 → A4 → VENTA
      ↓     ↑     ↓
      A5 ←──┴─────┘
      ↓
      A6 (si hay incidencias)`}
          </pre>
        </div>
      </div>

      {/* KPIs Principales */}
      {overviewData && (
        <>
          <div style={styles.metricsGrid}>
            <Metric
              title="Stock Total"
              value={overviewData.total_stock}
              format="number"
              color={getColor('primary.blue')}
            />
            <Metric
              title="Valor Total"
              value={overviewData.total_valor}
              format="currency"
              color={getColor('accent.green')}
            />
            <Metric
              title="SKUs Únicos"
              value={overviewData.total_skus}
              format="number"
              color={getColor('primary.darkBlue')}
            />
            <Metric
              title="Productos Stock Crítico"
              value={overviewData.productos_stock_critico}
              format="number"
              color={getColor('accent.orange')}
            />
          </div>

          {/* Gráficos */}
          <div style={styles.chartsGrid}>
            <Card style={{ padding: getSpacing('lg') }}>
              <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>📈 Stock por Almacén</h3>
              <Chart
                data={[{
                  x: Object.keys(overviewData.resumen_almacenes),
                  y: Object.values(overviewData.resumen_almacenes).map(almacen => almacen.Stock_Total),
                  type: 'bar',
                  marker: { color: getColor('primary.blue') }
                }]}
                layout={{
                  title: 'Distribución de Stock por Almacén',
                  xaxis: { title: 'Almacén' },
                  yaxis: { title: 'Stock Total' }
                }}
                height={300}
              />
            </Card>

            <Card style={{ padding: getSpacing('lg') }}>
              <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>💰 Valor por Almacén</h3>
              <Chart
                data={[{
                  values: Object.values(overviewData.resumen_almacenes).map(almacen => almacen.Valor_Total),
                  labels: Object.keys(overviewData.resumen_almacenes),
                  type: 'pie'
                }]}
                layout={{
                  title: 'Distribución de Valor por Almacén'
                }}
                height={300}
              />
            </Card>
          </div>

          {/* Tabla de Resumen */}
          <Card style={{ padding: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>📋 Resumen Detallado por Almacén</h3>
            <DataTable
              data={Object.entries(overviewData.resumen_almacenes).map(([almacen, data]) => ({
                Almacén: almacen,
                'Stock Total': data.Stock_Total.toLocaleString(),
                'Valor Total': `$${data.Valor_Total.toLocaleString()}`
              }))}
              columns={['Almacén', 'Stock Total', 'Valor Total']}
            />
          </Card>
        </>
      )}
    </div>
  );

  // Componente para tabs en desarrollo
  const PlaceholderTab = ({ title, description, intention }) => (
    <div style={styles.placeholderContent}>
      <h3 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('md') }}>{title}</h3>
      <p style={{ fontSize: getFontSize('base'), marginBottom: getSpacing('md') }}>{description}</p>
      {intention && (
        <p style={{ fontSize: getFontSize('sm'), color: getColor('primary.blue') }}>
          <strong>{intention}</strong>
        </p>
      )}
      <p style={{ fontSize: getFontSize('sm'), marginTop: getSpacing('md') }}>
        🚧 Funcionalidad en desarrollo - Próximamente disponible
      </p>
    </div>
  );

  // Componente Movimientos Programáticos (implementación completa)
  const MovimientosProgramaticosTab = () => (
    <div>
      <div style={{ marginBottom: getSpacing('lg') }}>
        <h2 style={{ fontSize: getFontSize('xl'), marginBottom: getSpacing('sm') }}>
          🔄 Movimientos Programáticos entre Almacenes
        </h2>
        <p style={{ color: getColor('neutral.mediumGray') }}>
          <strong>Intención 3b</strong>: Registrar salidas de A2 y entradas a A3/A4/A5 sin hojas manuales
        </p>
      </div>

      {/* Controles de filtros */}
      <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>Filtros de Análisis</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: getSpacing('md') }}>
          <div>
            <label style={styles.label}>Período de análisis:</label>
            <select style={styles.multiselect}>
              <option value="7">7 días</option>
              <option value="15">15 días</option>
              <option value="30" selected>30 días</option>
              <option value="60">60 días</option>
              <option value="90">90 días</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Almacén origen:</label>
            <select style={styles.multiselect}>
              <option value="Todos">Todos</option>
              <option value="A2">A2</option>
              <option value="A3">A3</option>
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Almacén destino:</label>
            <select style={styles.multiselect}>
              <option value="Todos">Todos</option>
              <option value="A2">A2</option>
              <option value="A3">A3</option>
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </select>
          </div>
        </div>
      </Card>

      {/* KPIs de movimientos */}
      <div style={styles.metricsGrid}>
        <Metric title="Total Movimientos" value={1247} format="number" />
        <Metric title="Unidades Movidas" value={28345} format="number" />
        <Metric title="Valor Total" value={567890.45} format="currency" />
        <Metric title="SKUs Únicos" value={456} format="number" />
      </div>

      {/* Gráficos de análisis */}
      <div style={styles.chartsGrid}>
        <Card style={{ padding: getSpacing('lg') }}>
          <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>📊 Movimientos por Flujo</h3>
          <Chart
            data={[{
              x: ['A2→A3', 'A3→A4', 'A4→A5', 'A5→A2', 'A2→A5'],
              y: [342, 298, 156, 89, 67],
              type: 'bar',
              marker: { color: getColor('primary.blue') }
            }]}
            layout={{
              title: 'Número de Movimientos por Flujo',
              xaxis: { title: 'Flujo', tickangle: 45 },
              yaxis: { title: 'Número de Movimientos' }
            }}
            height={300}
          />
        </Card>

        <Card style={{ padding: getSpacing('lg') }}>
          <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>💰 Valor por Flujo</h3>
          <Chart
            data={[{
              values: [234567, 198432, 89765, 34567, 23456],
              labels: ['A2→A3', 'A3→A4', 'A4→A5', 'A5→A2', 'A2→A5'],
              type: 'pie'
            }]}
            layout={{
              title: 'Distribución de Valor por Flujo'
            }}
            height={300}
          />
        </Card>
      </div>

      {/* Simulador */}
      <Card style={{ padding: getSpacing('lg') }}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>🎮 Simulador de Movimiento Programático</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: getSpacing('md') }}>
          <div>
            <label style={styles.label}>SKU:</label>
            <select style={styles.multiselect}>
              <option>GDS-0001A</option>
              <option>GDS-0002B</option>
              <option>GDS-0003C</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Almacén origen:</label>
            <select style={styles.multiselect}>
              <option>A2</option>
              <option>A3</option>
              <option>A4</option>
              <option>A5</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Almacén destino:</label>
            <select style={styles.multiselect}>
              <option>A3</option>
              <option>A4</option>
              <option>A5</option>
              <option>A2</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Cantidad:</label>
            <input 
              type="number" 
              min="1" 
              max="1000" 
              defaultValue="10"
              style={{
                ...styles.multiselect,
                padding: getSpacing('sm'),
              }}
            />
          </div>
        </div>
        <Button 
          style={{ marginTop: getSpacing('md') }}
          onClick={() => alert('Movimiento simulado creado')}
        >
          🚀 Simular Movimiento
        </Button>
      </Card>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          📦 Análisis de Inventarios y Movimientos de Entrada
        </h1>
      </div>

      {/* Control de fuente de datos */}
      <div style={styles.sidebar}>
        <h3 style={styles.sidebarTitle}>
          🗄️ Configuración de Datos
        </h3>
        
        <div style={styles.toggle}>
          <div style={styles.toggleSwitch} onClick={handleToggleSynthetic}>
            <div style={styles.toggleButton} />
          </div>
          <span>Usar datos sintéticos</span>
        </div>
        
        {useSynthetic ? (
          <div style={styles.infoCard}>
            🧪 Usando datos sintéticos<br />
            <small>Registros totales: {overviewData?.total_records || 0}</small><br />
            <small>Tablas: {overviewData?.tables?.length || 0}</small>
          </div>
        ) : (
          <div style={styles.successCard}>
            🗄️ Conectado a ggs_desarrollo<br />
            <small>Última conexión: {overviewData?.last_connection || 'N/A'}</small>
          </div>
        )}

        <div style={styles.controlSection}>
          <label style={styles.label}>Almacenes a analizar:</label>
          <select 
            multiple 
            value={selectedAlmacenes}
            onChange={handleAlmacenesChange}
            style={{
              ...styles.multiselect,
              height: '80px',
            }}
          >
            {almacenesDisponibles.map(almacen => (
              <option key={almacen} value={almacen}>{almacen}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Pestañas principales */}
      <Tabs activeTab={activeTab} onTabChange={setActiveTab} variant="underline">
        <Tabs.TabPanel label="🏠 Dashboard" icon="🏠">
          <DashboardTab />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="🔍 Equivalencias GASHPE-Sinube" icon="🔍">
          <PlaceholderTab 
            title="🔍 Equivalencias GASHPE-Sinube"
            description="Validar equivalencia entre intenciones de compra en GASHPE y Sinube por SKU"
            intention="Intención 0"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="📋 Vista Excel OCs" icon="📋">
          <PlaceholderTab 
            title="📋 Vista Excel de Órdenes de Compra"
            description="Mostrar tabla por SKU que imite el Excel de OCs"
            intention="Intención 3a"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="🔄 Movimientos Programáticos" icon="🔄">
          <MovimientosProgramaticosTab />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="📦 Recepciones y Devoluciones" icon="📦">
          <PlaceholderTab 
            title="📦 Recepciones y Devoluciones"
            description="Resolver diferencias de recepción, devoluciones y nuevas entregas sobre una misma OC"
            intention="Intención 2"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="⚠️ Pérdidas Marketplace" icon="⚠️">
          <PlaceholderTab 
            title="⚠️ Pérdidas por Marketplace"
            description="Registrar pérdidas en unidades y monetario"
            intention="Intención 4"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="💰 Análisis Rentabilidad" icon="💰">
          <PlaceholderTab 
            title="💰 Análisis de Rentabilidad"
            description="Simulación de rentabilidad por SKU"
            intention="Intención -1"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="📈 Costos de Venta" icon="📈">
          <PlaceholderTab 
            title="📈 Costos de Venta"
            description="Módulo de costo de venta mensual por SKU"
            intention="Intención x2"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="🔍 Consistencia Inventarios" icon="🔍">
          <PlaceholderTab 
            title="🔍 Consistencia de Inventarios"
            description="Verificar consistencia de salidas vs stock en A4/A5"
            intention="Intención x1"
          />
        </Tabs.TabPanel>
        
        <Tabs.TabPanel label="⚙️ Configuración" icon="⚙️">
          <PlaceholderTab 
            title="⚙️ Configuración y Herramientas"
            description="Configuración del sistema, validaciones y exportación de reportes"
            intention="Herramientas administrativas"
          />
        </Tabs.TabPanel>
      </Tabs>
    </div>
  );
};

export default Movimientos;